// ============================================================================
// APPLICATIONS DES APPAREILS — nos applications installables, leur nom au store, et ce que le store en dit.
//
// UTILITÉ : la construction dépose chaque APK sous le nom de l'application (`@orgazone/applications`), le même dans le
// store de bêta et dans celui de production. Ce bloc montre ce nom — à copier pour créer l'application au store du
// premier coup — et le rapprochement avec le store de cet environnement (`GET /v1/store-apps`) : présente avec ses
// groupes et sa version publique, absente (avec le nom à créer), ou portée sous un autre nom.
//
// ⚠️ §23.0 : un store illisible se DIT, il ne devient ni « présente » ni « absente ». Et une application du store qui
//    ressemble à l'une des nôtres sous un autre nom est nommée comme telle : la construction n'y déposera jamais.
// ⚠️ CE BLOC NE CRÉE RIEN AU STORE : le nom est figé à la création et c'est un geste de l'éditeur, dans
//    l'administration du store. La Console constate, et dit quoi créer.
// ============================================================================
import { useEffect, useState, type CSSProperties } from 'react';
import { APP_STATE_TEXT } from '@orgazone/applications';
import { Copyable } from '@orgazone/ui-web';
import { api, type StoreAppLine, type StoreAppsSurvey } from '../api';
import { NATURE_LABEL } from '../equipment-types';
import { Pill, type Tone } from '../ui';

const noteSt: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55 };

/** Ce que l'écran dit d'une application face au store : une pastille et des phrases. */
export interface StoreVerdict { tone: Tone; pill: string; lines: string[] }

/** Le verdict d'une ligne — la règle de ce bloc, sans rendu, pour que le banc la tienne. */
export function storeVerdict(line: StoreAppLine, storeUrl: string | null): StoreVerdict {
  const where = storeUrl ? `le store ${storeUrl}` : 'le store de cet environnement';
  const st = line.store;
  if (st.kind === 'unknown') return { tone: 'wa', pill: 'store illisible', lines: [`Rien n’est affirmé : ${st.error}`] };
  if (st.kind === 'absent') {
    const lookalike = st.lookalike
      ? [`${where} porte « ${st.lookalike.slug} » (${st.lookalike.name}) : un AUTRE nom, où la construction ne déposera jamais. Le nom est figé au store : créez l’application sous « ${line.slug} », puis retirez l’autre si elle est vide.`]
      : [];
    if (!line.installable) {
      return { tone: 'ne', pill: 'pas encore au store', lines: [`À créer dans ${where} sous « ${line.slug} » quand elle sera construite.`, ...lookalike] };
    }
    return {
      tone: 'da', pill: 'absente du store',
      lines: [`À créer dans l’administration de ${where} sous le nom exact « ${line.slug} » : la construction s’arrête tant qu’elle y manque.`, ...lookalike],
    };
  }
  const e = st.entry;
  const groups = e.groups.length === 0 ? 'aucun groupe'
    : `${e.groups.length} groupe${e.groups.length > 1 ? 's' : ''} : ${e.groups.map((g) => `${g.name} (${g.servedVersion ?? 'flux public'})`).join(' · ')}`;
  return {
    tone: 'ok', pill: 'au store',
    lines: [
      e.publicVersion ? `Version publique : ${e.publicVersion}` : 'Rien de déployé au flux public',
      groups,
      ...(e.isPublished ? [] : ['Non publiée au catalogue du store.']),
    ],
  };
}

/** Une ligne : l'application, son nom au store à copier, et ce que le store en dit. */
function DeviceAppRow({ line, storeUrl }: { line: StoreAppLine; storeUrl: string | null }) {
  const v = storeVerdict(line, storeUrl);
  return (
    <div data-device-app={line.slug} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 18px', padding: '13px 16px', borderTop: '1px solid var(--ink200)' }}>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{line.name}</span>
          <Pill tone={line.state === 'prod' ? 'ok' : 'ne'} shape="tight">{APP_STATE_TEXT[line.state]}</Pill>
        </div>
        <div style={noteSt}>{line.description}</div>
        <div style={noteSt}>Appareil : {line.nature ? NATURE_LABEL[line.nature] : 'à définir'}</div>
      </div>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>Nom au store</div>
        <Copyable value={line.slug} label={`Copier le nom « ${line.slug} »`}>
          <span className="mono" style={{ fontSize: 'var(--t-note)', fontWeight: 600 }}>{line.slug}</span>
        </Copyable>
      </div>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div><Pill tone={v.tone} shape="tight">{v.pill}</Pill></div>
        {v.lines.map((l) => <div key={l} style={{ ...noteSt, overflowWrap: 'anywhere' }}>{l}</div>)}
      </div>
    </div>
  );
}

/** Le bloc de l'écran Applications : nos applications installables et le store de cet environnement. */
export function DeviceAppsSection() {
  const [survey, setSurvey] = useState<StoreAppsSurvey | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.storeApps().then((r) => { if (alive) setSurvey(r); }).catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  return (
    <section className="dcard" aria-labelledby="device-apps-title" style={{ overflow: 'hidden', marginTop: 14 }}>
      <div style={{ padding: '13px 16px', background: 'var(--head)' }}>
        <h2 id="device-apps-title" style={{ margin: 0, fontSize: 'var(--t-control)', fontWeight: 700 }}>Applications des appareils</h2>
        <div style={{ ...noteSt, marginTop: 3 }}>
          Les applications que le store privé distribue aux caisses et aux terminaux. Le nom au store est celui sous lequel
          la construction dépose l’APK, en bêta comme en production : une application se crée au store sous ce nom exact,
          qui ne se change plus ensuite.
        </div>
        {survey && (
          <div className="mono" style={{ ...noteSt, marginTop: 6, overflowWrap: 'anywhere' }}>
            {survey.storeUrl ? `Store lu : ${survey.storeUrl}` : 'Adresse du store non configurée (STORE_API_URL).'}
          </div>
        )}
      </div>
      {err !== null && <div className="err" style={{ margin: 16 }}>La Console n’a pas pu lire ses applications : {err}</div>}
      {!survey && err === null && <div className="muted" style={{ padding: 16 }}>Lecture du store…</div>}
      {survey?.lines.map((l) => <DeviceAppRow key={l.slug} line={l} storeUrl={survey.storeUrl} />)}
      {survey && survey.orphans.length > 0 && (
        <div data-block="orphans" style={{ padding: '13px 16px', borderTop: '1px solid var(--ink200)', background: 'var(--panel)' }}>
          <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginBottom: 6 }}>Le store porte aussi</div>
          {survey.orphans.map((o) => (
            <div key={o.slug} style={noteSt}>
              <span className="mono">{o.slug}</span> ({o.name}) — aucune de nos applications ne porte ce nom : la construction n’y dépose rien.
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
